import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  RefreshCw,
  Trophy,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Users,
  ShoppingBag,
  DollarSign,
  PieChart as PieChartIcon,
  CreditCard,
  Filter,
  Printer,
  X,
  ChevronLeft,
} from "lucide-react";
import {
  ResponsiveContainer,
  ComposedChart,
  Area,
  Bar,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
} from "recharts";
import { extractBusinessType, getStoredBusinessType } from "../utils/businessType";
import { apiFetch } from "../utils/apiClient";
import { buildZReportPrintHtml } from "../shared/zReportPrint";
import StockSituationPanel from "./StockSituationPanel";
import { getUserFacingError } from "../utils/userFacingError";

const currencyNumberFormatter = new Intl.NumberFormat("fr-FR", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});
const formatCurrency = (value = 0) =>
  `${currencyNumberFormatter.format(Number(value) || 0)} FCFA`;

const numberFormatter = new Intl.NumberFormat("fr-FR", {
  minimumFractionDigits: 0,
});

const normalizePaymentFeatureCode = (method = {}) =>
  String(method?.code || method?.id || method?.name || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "");

export const filterDashboardPaymentTotals = (paymentTotals = [], features = {}) => {
  const aggregatorsEnabled = features?.aggregators_enabled === true;
  const yangoEnabled = aggregatorsEnabled && features?.aggregator_yango_enabled === true;
  const glovoEnabled = aggregatorsEnabled && features?.aggregator_glovo_enabled === true;
  return (Array.isArray(paymentTotals) ? paymentTotals : []).filter((method) => {
    const code = normalizePaymentFeatureCode(method);
    if (code.includes("yango")) return yangoEnabled;
    if (code.includes("glovo")) return glovoEnabled;
    return true;
  });
};

const printHtmlDocument = (html) =>
  new Promise((resolve, reject) => {
    try {
      const iframe = document.createElement("iframe");
      iframe.style.position = "fixed";
      iframe.style.right = "0";
      iframe.style.bottom = "0";
      iframe.style.width = "0";
      iframe.style.height = "0";
      iframe.style.border = "0";
      iframe.onload = () => {
        try {
          const win = iframe.contentWindow;
          if (!win) throw new Error("Fenêtre d'impression indisponible");
          setTimeout(() => {
            win.focus();
            win.print();
            setTimeout(() => iframe.remove(), 800);
            resolve(true);
          }, 150);
        } catch (error) {
          iframe.remove();
          reject(error);
        }
      };
      document.body.appendChild(iframe);
      iframe.contentDocument.open();
      iframe.contentDocument.write(html);
      iframe.contentDocument.close();
    } catch (error) {
      reject(error);
    }
  });

const AdminInterface = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [businessType, setBusinessType] = useState(getStoredBusinessType);
  const [printingZ, setPrintingZ] = useState(false);
  const [showOpenOrders, setShowOpenOrders] = useState(false);
  const [selectedOpenTable, setSelectedOpenTable] = useState(null);
  const isBoutique = businessType === "boutique";
  const [productsLimit, setProductsLimit] = useState(5);
  const alertsTrackRef = useRef(null);
  const alertsAutoTimerRef = useRef(null);
  const alertsResumeTimerRef = useRef(null);
  const alertsPausedRef = useRef(false);
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined"
      ? window.matchMedia("(max-width: 767px)").matches
      : false,
  );
  const [chartMetric, setChartMetric] = useState("all");
  const [isDarkMode, setIsDarkMode] = useState(() =>
    typeof document !== "undefined"
      ? document.documentElement.classList.contains("dark")
      : false,
  );

  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const target = document.documentElement;
    const observer = new MutationObserver(() => {
      setIsDarkMode(target.classList.contains("dark"));
    });
    observer.observe(target, { attributes: true, attributeFilter: ["class"] });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!showOpenOrders) return undefined;
    const closeOnEscape = (event) => {
      if (event.key === "Escape") {
        if (selectedOpenTable) setSelectedOpenTable(null);
        else setShowOpenOrders(false);
      }
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [selectedOpenTable, showOpenOrders]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const media = window.matchMedia("(max-width: 767px)");
    const handleChange = () => setIsMobile(media.matches);
    handleChange();
    if (media.addEventListener) {
      media.addEventListener("change", handleChange);
      return () => media.removeEventListener("change", handleChange);
    }
    media.addListener(handleChange);
    return () => media.removeListener(handleChange);
  }, []);

  useEffect(() => {
    setChartMetric((prev) => {
      if (isMobile) return prev === "all" ? "sales" : prev;
      return "all";
    });
  }, [isMobile]);

  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiFetch("/api/dashboard/stats?productsLimit=30", {
        cache: "no-store",
      });
      if (!response.ok) {
        throw new Error("Impossible de charger les statistiques");
      }
      const data = await response.json();
      setStats(data);
      setLastUpdate(new Date());
    } catch (err) {
      console.error("❌ Dashboard error:", err);
      setError(getUserFacingError(err, "Tableau de bord momentanément indisponible."));
    } finally {
      setLoading(false);
    }
  };

  const handlePrintZReport = async () => {
    if (printingZ) return;
    try {
      setPrintingZ(true);
      setError(null);
      const response = await apiFetch(
        "/api/reports/z-report",
        { cache: "no-store", includeBearer: true, tokenMode: "admin" },
      );
      if (!response.ok) {
        throw new Error("Impossible de charger le rapport Z");
      }
      const report = await response.json();
      const restaurantName =
        stats?.restaurant?.name ||
        stats?.restaurant_name ||
        stats?.business?.name ||
        "MOO Sync";
      await printHtmlDocument(buildZReportPrintHtml({
        report,
        restaurantName,
        printedBy: "Administration",
      }));
    } catch (err) {
      console.error("Erreur impression Z:", err);
      setError(err.message || "Erreur impression Z");
    } finally {
      setPrintingZ(false);
    }
  };

  useEffect(() => {
    const loadBusinessType = async () => {
      try {
        const res = await fetch("/api/restaurant-info");
        if (!res.ok) return;
        const data = await res.json();
        const nextType = extractBusinessType(data);
        setBusinessType(nextType);
        if (typeof localStorage !== "undefined") {
          try {
            localStorage.setItem("restaurant-info", JSON.stringify(data));
          } catch (_e) {}
        }
      } catch (_e) {
        // ignore
      }
    };
    loadBusinessType();
  }, []);

  useEffect(() => {
    fetchDashboardStats();
    const interval = setInterval(fetchDashboardStats, 60000);
    return () => clearInterval(interval);
  }, []);

  const displayedProducts = useMemo(() => {
    if (!stats?.trendingProducts?.items) return [];
    return stats.trendingProducts.items.slice(0, productsLimit);
  }, [stats, productsLimit]);

  const displayedPaymentTotals = useMemo(
    () => filterDashboardPaymentTotals(stats?.paymentTotals, stats?.features),
    [stats?.paymentTotals, stats?.features],
  );

  const alerts = useMemo(() => {
    if (!stats?.alerts) return [];
    const items = [];
    stats.alerts.products?.forEach((alert) => {
      items.push({
        type: "product",
        icon: Trophy,
        accent: "from-emerald-400/20 to-emerald-600/20",
        border: "border-emerald-400/40",
        title: alert.label,
        message: `${alert.name} - ${numberFormatter.format(alert.quantity)} ventes`,
        delta: alert.previousRecord
          ? alert.quantity - alert.previousRecord
          : alert.quantity,
      });
    });
    if (!isBoutique) {
      stats.alerts.visitors?.forEach((alert) => {
        items.push({
          type: "visitors",
          icon: Users,
          accent: "from-sky-400/20 to-blue-600/20",
          border: "border-sky-400/40",
          title: alert.label,
          message: `${numberFormatter.format(alert.value)} visiteurs`,
          delta: alert.previousRecord
            ? alert.value - alert.previousRecord
            : alert.value,
        });
      });
    }
    stats.alerts.services?.forEach((alert) => {
      items.push({
        type: "services",
        icon: ShoppingBag,
        accent: "from-amber-400/20 to-orange-600/20",
        border: "border-amber-400/40",
        title: alert.label,
        message: `${numberFormatter.format(alert.value)} services`,
        delta: alert.previousRecord
          ? alert.value - alert.previousRecord
          : alert.value,
      });
    });
    return items;
  }, [stats, isBoutique]);

  useEffect(() => {
    const container = alertsTrackRef.current;
    if (!container || alerts.length < 2) return undefined;
    if (typeof window === "undefined") return undefined;

    const prefersReduced = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
    if (prefersReduced) return undefined;
    if (!isMobile) return undefined;

    const getStep = () => {
      const card = container.querySelector("[data-alert-card]");
      if (!card) return container.clientWidth;
      const styles = window.getComputedStyle(container);
      const gap = parseFloat(styles.columnGap || styles.gap || "0");
      return card.getBoundingClientRect().width + gap;
    };

    const startAuto = () => {
      if (alertsAutoTimerRef.current) return;
      alertsAutoTimerRef.current = window.setInterval(() => {
        if (alertsPausedRef.current) return;
        const step = getStep();
        if (!step) return;
        const currentIndex = Math.round(container.scrollLeft / step);
        const nextIndex = (currentIndex + 1) % alerts.length;
        container.scrollTo({ left: nextIndex * step, behavior: "smooth" });
      }, 4200);
    };

    const stopAuto = () => {
      if (alertsAutoTimerRef.current) {
        window.clearInterval(alertsAutoTimerRef.current);
        alertsAutoTimerRef.current = null;
      }
    };

    const pause = () => {
      alertsPausedRef.current = true;
      stopAuto();
      if (alertsResumeTimerRef.current) {
        window.clearTimeout(alertsResumeTimerRef.current);
      }
    };

    const resume = () => {
      if (alertsResumeTimerRef.current) {
        window.clearTimeout(alertsResumeTimerRef.current);
      }
      alertsResumeTimerRef.current = window.setTimeout(() => {
        alertsPausedRef.current = false;
        startAuto();
      }, 2400);
    };

    startAuto();
    container.addEventListener("pointerdown", pause);
    container.addEventListener("touchstart", pause);
    container.addEventListener("wheel", pause, { passive: true });
    container.addEventListener("pointerup", resume);
    container.addEventListener("touchend", resume);
    container.addEventListener("mouseleave", resume);

    return () => {
      stopAuto();
      if (alertsResumeTimerRef.current) {
        window.clearTimeout(alertsResumeTimerRef.current);
        alertsResumeTimerRef.current = null;
      }
      container.removeEventListener("pointerdown", pause);
      container.removeEventListener("touchstart", pause);
      container.removeEventListener("wheel", pause);
      container.removeEventListener("pointerup", resume);
      container.removeEventListener("touchend", resume);
      container.removeEventListener("mouseleave", resume);
    };
  }, [alerts, isMobile]);

  const kpis = stats?.kpis;
  const openOrderDetails = Array.isArray(stats?.openOrders?.orders)
    ? stats.openOrders.orders
    : [];
  const chartData = stats?.hourly?.length ? stats.hourly : [];
  const chartTabs = [
    {
      id: "sales",
      label: "Ventes",
      dot: "bg-orange-400",
      active: "border-orange-400/60 text-orange-100",
    },
    {
      id: "orders",
      label: "Commandes",
      dot: "bg-indigo-400",
      active: "border-indigo-400/60 text-indigo-100",
    },
    {
      id: "visitors",
      label: "Visiteurs",
      dot: "bg-emerald-400",
      active: "border-emerald-400/60 text-emerald-100",
    },
    {
      id: "all",
      label: "Tout",
      dot: "bg-white/60",
      active: "border-white/40 text-white",
    },
  ].filter((tab) => !isBoutique || tab.id !== "visitors");
  const showSales = chartMetric === "all" || chartMetric === "sales";
  const showOrders = chartMetric === "all" || chartMetric === "orders";
  const showVisitors = !isBoutique && (chartMetric === "all" || chartMetric === "visitors");
  const useRightAxis = !isMobile || chartMetric === "all";

  useEffect(() => {
    if (isBoutique && chartMetric === "visitors") {
      setChartMetric("sales");
    }
  }, [chartMetric, isBoutique]);

  const formatAxisValue = (value, isCurrency) => {
    const numeric = Number(value) || 0;
    if (!isCurrency) return numberFormatter.format(numeric);
    const abs = Math.abs(numeric);
    if (abs >= 1_000_000) {
      return `${(numeric / 1_000_000).toFixed(1).replace(".0", "")}M`;
    }
    if (abs >= 1_000) {
      return `${(numeric / 1_000).toFixed(1).replace(".0", "")}k`;
    }
    return numberFormatter.format(numeric);
  };

  const cardBackground = isDarkMode
    ? "bg-[#0c1428] border border-white/10"
    : "bg-white border border-gray-100";

  return (
    <div
      className={`min-h-screen ${
        isDarkMode
          ? "bg-gradient-to-b from-[#030712] via-[#050d1f] to-[#030712] text-slate-100"
          : "bg-gradient-to-b from-slate-50 via-white to-slate-100 text-slate-900"
      }`}
    >
      <div className="px-3 sm:px-6 py-5 sm:py-8 space-y-6 max-w-7xl mx-auto">
        <header className={`${cardBackground} rounded-3xl p-6 shadow-lg`}>
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <p className="text-xs uppercase tracking-[0.35em] text-amber-300">
                Tableau de bord
              </p>
              <h1 className="text-2xl font-bold">
                {stats?.serviceDay?.label || "Journée en cours"}
              </h1>
              {stats?.serviceDay?.timeWindowLabel && (
                <p className={`${isDarkMode ? "text-white/60" : "text-slate-500"}`}>
                  Fenêtre de service : {stats.serviceDay.timeWindowLabel}
                </p>
              )}
            </div>
            <div className="flex items-center gap-3">
              {error && (
                <span className="text-sm text-rose-400">{error}</span>
              )}
              <button
                type="button"
                onClick={fetchDashboardStats}
                disabled={loading}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-2xl text-sm font-semibold bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow-lg hover:from-orange-600 hover:to-amber-600 disabled:opacity-50"
              >
                <RefreshCw
                  className={`w-4 h-4 ${loading ? "animate-spin" : ""}`}
                />
                Actualiser
              </button>
              <button
                type="button"
                onClick={handlePrintZReport}
                disabled={printingZ}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-2xl text-sm font-semibold bg-gradient-to-r from-slate-700 to-slate-900 text-white shadow-lg hover:from-slate-800 hover:to-black disabled:opacity-50"
              >
                <Printer
                  className={`w-4 h-4 ${printingZ ? "animate-pulse" : ""}`}
                />
                {printingZ ? "Impression..." : "Imprimer le Z"}
              </button>
            </div>
          </div>
          {lastUpdate && (
            <p className={`text-xs mt-2 ${isDarkMode ? "text-white/50" : "text-slate-500"}`}>
              Dernière mise à jour : {lastUpdate.toLocaleTimeString("fr-FR")}
            </p>
          )}
        </header>

        {alerts.length > 0 && (
          <section>
            <div className="relative md:hidden -mx-6">
              <div
                ref={alertsTrackRef}
                className="flex gap-4 overflow-x-auto px-6 pb-2 snap-x snap-mandatory scroll-smooth [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
              >
                {alerts.map((alert, index) => (
                  <div
                    key={`${alert.type}-${index}`}
                    data-alert-card
                    className={`shrink-0 min-w-[78%] rounded-2xl border ${alert.border} bg-gradient-to-r ${alert.accent} p-4 backdrop-blur snap-center`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="rounded-2xl bg-black/10 p-3">
                        <alert.icon className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="text-xs uppercase tracking-[0.3em] opacity-80">
                          {alert.title}
                        </p>
                        <p className="text-sm font-semibold">{alert.message}</p>
                        {alert.delta !== undefined && (
                          <p className="text-xs opacity-80">
                            +{numberFormatter.format(alert.delta)} vs précédent record
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div
                className={`pointer-events-none absolute inset-y-0 left-0 w-6 bg-gradient-to-r ${
                  isDarkMode ? "from-[#050d1f]" : "from-white"
                } to-transparent`}
              />
              <div
                className={`pointer-events-none absolute inset-y-0 right-0 w-6 bg-gradient-to-l ${
                  isDarkMode ? "from-[#050d1f]" : "from-white"
                } to-transparent`}
              />
            </div>

            <div className="hidden md:grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {alerts.map((alert, index) => (
                <div
                  key={`${alert.type}-${index}`}
                  className={`rounded-2xl border ${alert.border} bg-gradient-to-r ${alert.accent} p-4 backdrop-blur`}
                >
                  <div className="flex items-center gap-3">
                    <div className="rounded-2xl bg-black/10 p-3">
                      <alert.icon className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-[0.3em] opacity-80">
                        {alert.title}
                      </p>
                      <p className="text-sm font-semibold">{alert.message}</p>
                      {alert.delta !== undefined && (
                        <p className="text-xs opacity-80">
                          +{numberFormatter.format(alert.delta)} vs précédent record
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        <section
          className={`grid grid-cols-1 md:grid-cols-2 ${
            isBoutique ? "xl:grid-cols-3" : "xl:grid-cols-4"
          } gap-4`}
        >
          {kpis && (
            <>
              <KpiCard
                title="Chiffre d'affaires"
                value={formatCurrency(kpis.revenue.value || 0)}
                delta={<KpiDelta entry={kpis.revenue} />}
                meta={
                  <span className="block space-y-1">
                    {stats?.features?.credit_sale_enabled ? (
                      <span className="block">
                        dont {formatCurrency(kpis.revenue.creditSales || 0)} à crédit
                      </span>
                    ) : null}
                    <span className="block">
                      À encaisser : {formatCurrency(stats?.openOrders?.value || 0)}
                    </span>
                  </span>
                }
                icon={DollarSign}
                isDarkMode={isDarkMode}
              />
              <KpiCard
                title="Commandes / Services"
                value={numberFormatter.format(kpis.orders.value || 0)}
                delta={<KpiDelta entry={kpis.orders} />}
                icon={ShoppingBag}
                isDarkMode={isDarkMode}
              />
              {!isBoutique && (
                <KpiCard
                  title="Visiteurs"
                  value={numberFormatter.format(kpis.visitors.value || 0)}
                  delta={<KpiDelta entry={kpis.visitors} />}
                  icon={Users}
                  isDarkMode={isDarkMode}
                />
              )}
              <KpiCard
                title="Panier moyen"
                value={formatCurrency(kpis.averageTicket.value || 0)}
                delta={<KpiDelta entry={kpis.averageTicket} />}
                icon={PieChartIcon}
                isDarkMode={isDarkMode}
              />
            </>
          )}
        </section>

        {Number(stats?.openOrders?.count || 0) > 0 && (
          <button
            type="button"
            onClick={() => setShowOpenOrders(true)}
            className={`${cardBackground} w-full rounded-3xl p-4 text-left shadow-lg transition hover:border-amber-300/40 sm:p-6`}
          >
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-xs uppercase tracking-[0.3em] text-amber-300">
                  À encaisser
                </p>
                <h2 className="text-lg font-semibold tracking-tight">
                  Tables occupées à encaisser
                </h2>
                <p className="mt-1 text-sm text-slate-400">Toucher pour afficher la liste</p>
              </div>
              <div className="rounded-2xl border border-amber-300/20 bg-amber-300/10 px-3 py-2 text-right">
                <p className="text-lg font-bold text-amber-300">
                  {formatCurrency(stats?.openOrders?.value || 0)}
                </p>
                <p className="text-xs text-slate-400">
                  {numberFormatter.format(stats?.openOrders?.count || 0)} table
                  {Number(stats?.openOrders?.count || 0) > 1 ? "s" : ""}
                </p>
              </div>
            </div>
          </button>
        )}

        <section className={`${cardBackground} rounded-3xl p-4 sm:p-6 shadow-lg`}>
          <div className="flex flex-col gap-3 mb-4">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-amber-300">
                Tendances horaires
              </p>
              <h2 className="text-[15px] sm:text-lg font-semibold tracking-tight whitespace-nowrap">
                {isBoutique ? "Ventes · Commandes" : "Ventes · Commandes · Visiteurs"}
              </h2>
            </div>
            <div className="-mx-1 sm:mx-0">
              <div className="flex items-center gap-2 overflow-x-auto px-1 pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                {chartTabs.map((tab) => {
                  if (!isMobile && tab.id === "all") {
                    return null;
                  }
                  const isActive = isMobile ? chartMetric === tab.id : true;
                  return (
                    <button
                      key={tab.id}
                      type="button"
                      onClick={() => {
                        if (isMobile) setChartMetric(tab.id);
                      }}
                      className={`inline-flex items-center gap-2 whitespace-nowrap rounded-full px-3 py-1 text-xs font-semibold border transition ${
                        isActive
                          ? `${tab.active} bg-white/5`
                          : "border-white/10 bg-white/5 text-white/60 hover:border-amber-300/40"
                      } ${isMobile ? "cursor-pointer" : "cursor-default"}`}
                      aria-pressed={isMobile ? isActive : undefined}
                    >
                      <span className={`h-2 w-2 rounded-full ${tab.dot}`} />
                      {tab.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
          {chartData.length === 0 ? (
            <p className="text-sm text-slate-500">
              Pas encore de données pour cette journée de service.
            </p>
          ) : (
            <div className="h-72 md:h-80 -mx-2 sm:mx-0">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart
                  data={chartData}
                  margin={{ top: 8, right: isMobile ? -6 : 24, bottom: 0, left: isMobile ? -6 : 0 }}
                >
                  <CartesianGrid
                    strokeDasharray="4 4"
                    stroke={isDarkMode ? "rgba(148,163,184,0.15)" : "rgba(15,23,42,0.08)"}
                  />
                  <XAxis
                    dataKey="hour"
                    interval={isMobile ? 2 : 0}
                    tickMargin={6}
                    tick={{ fill: isDarkMode ? "#cbd5f5" : "#1f2937", fontSize: isMobile ? 10 : 12 }}
                  />
                  <YAxis
                    yAxisId="left"
                    tickMargin={8}
                    tick={{ fill: isDarkMode ? "#cbd5f5" : "#1f2937", fontSize: isMobile ? 10 : 12 }}
                    tickFormatter={(value) =>
                      formatAxisValue(
                        value,
                        !isMobile || chartMetric === "sales" || chartMetric === "all",
                      )
                    }
                  />
                  <YAxis
                    yAxisId="right"
                    orientation="right"
                    hide={!useRightAxis}
                    tickMargin={8}
                    tick={{ fill: isDarkMode ? "#cbd5f5" : "#1f2937", fontSize: isMobile ? 10 : 12 }}
                    tickFormatter={(value) => formatAxisValue(value, false)}
                  />
                  <Tooltip
                    content={(props) => (
                      <DashboardTooltip
                        {...props}
                        activeMetrics={{
                          sales: showSales,
                          orders: showOrders,
                          visitors: showVisitors,
                        }}
                        isDarkMode={isDarkMode}
                      />
                    )}
                  />
                  {showSales && (
                    <Area
                      yAxisId="left"
                      type="monotone"
                      dataKey="sales"
                      stroke="#f97316"
                      strokeWidth={2}
                      fill="url(#salesGradient)"
                      name="Ventes"
                      activeDot={{ r: 4 }}
                    />
                  )}
                  {showOrders && (
                    <Bar
                      yAxisId={useRightAxis ? "right" : "left"}
                      dataKey="orders"
                      fill="#818cf8"
                      name="Commandes"
                      opacity={0.9}
                      barSize={isMobile ? 12 : 18}
                      radius={[10, 10, 0, 0]}
                    />
                  )}
                  {showVisitors && (
                    <Line
                      yAxisId={useRightAxis ? "right" : "left"}
                      type="monotone"
                      dataKey="visitors"
                      stroke="#34d399"
                      strokeWidth={2.5}
                      dot={false}
                      name="Visiteurs"
                    />
                  )}
                  <defs>
                    <linearGradient id="salesGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#f97316" stopOpacity={0.5} />
                      <stop offset="95%" stopColor="#f97316" stopOpacity={0.05} />
                    </linearGradient>
                  </defs>
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          )}
        </section>

        <StockSituationPanel
          initialData={stats?.dailyStockSituation}
          isDarkMode={isDarkMode}
        />

        <section className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className={`${cardBackground} rounded-3xl p-6 shadow-lg`}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-xs uppercase tracking-[0.3em] text-amber-300">
                  Produits en vogue
                </p>
                <h2 className="text-lg font-semibold">Session en cours</h2>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Filter className="w-4 h-4" />
                <select
                  value={productsLimit}
                  onChange={(e) => setProductsLimit(Number(e.target.value))}
                  className={`rounded-xl border px-3 py-1 ${
                    isDarkMode
                      ? "bg-white/5 border-white/15"
                      : "bg-white border-slate-200"
                  }`}
                >
                  {[5, 10, 15, 20].map((limit) => (
                    <option key={limit} value={limit}>
                      Top {limit}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            {displayedProducts.length === 0 ? (
              <p className="text-sm text-slate-500">
                Aucun produit enregistré pendant cette session.
              </p>
            ) : (
              <div className="space-y-3">
                {displayedProducts.map((product, index) => (
                  <div
                    key={product.id || index}
                    className={`flex items-center justify-between rounded-2xl px-4 py-3 ${
                      isDarkMode ? "bg-white/5" : "bg-white"
                    }`}
                  >
                    <div>
                      <p className="text-xs uppercase tracking-[0.3em] text-amber-300">
                        #{index + 1}
                      </p>
                      <p className="font-semibold">{product.name}</p>
                      <p className="text-xs text-slate-500">
                        {product.category || "Sans catégorie"}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold">
                        {numberFormatter.format(product.quantity)} ventes
                      </p>
                      <p className="text-xs text-slate-500">
                        {formatCurrency(product.revenue || 0)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className={`${cardBackground} rounded-3xl p-6 shadow-lg`}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-xs uppercase tracking-[0.3em] text-amber-300">
                  Classement des caissiers
                </p>
                <h2 className="text-lg font-semibold">CA par caissiers</h2>
              </div>
            </div>
            {stats?.staffRanking?.length ? (
              <div className="space-y-3">
                {stats.staffRanking.slice(0, 6).map((staff, index) => (
                  <div
                    key={staff.userId || index}
                    className={`flex items-center justify-between rounded-2xl px-4 py-3 ${
                      isDarkMode ? "bg-white/5" : "bg-white"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
                          isDarkMode ? "bg-white/10" : "bg-slate-100"
                        }`}
                      >
                        #{index + 1}
                      </div>
                      <div>
                        <p className="font-semibold">{staff.name}</p>
                        <p className="text-xs text-slate-500">
                          {staff.services} {isBoutique ? "tickets" : "services"}
                        </p>
                      </div>
                    </div>
                    <p className="font-semibold">
                      {formatCurrency(staff.totalSales || 0)}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-slate-500">
                {isBoutique
                  ? "Aucun ticket enregistré pour cette journée."
                  : "Aucun service enregistré pour cette journée."}
              </p>
            )}
          </div>
        </section>

        <section className={`${cardBackground} rounded-3xl p-6 shadow-lg`}>
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-amber-300">
                Paiements
              </p>
              <h2 className="text-lg font-semibold">Totaux par mode de paiement</h2>
            </div>
          </div>
          {displayedPaymentTotals.length ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {displayedPaymentTotals.map((method) => (
                <div
                  key={method.id}
                  className={`rounded-2xl border p-4 flex flex-col gap-2 ${
                    method.color ? "border-transparent" : "border-white/10"
                  }`}
                  style={{
                    background: method.color
                      ? `${method.color}1a`
                      : isDarkMode
                        ? "rgba(255,255,255,0.03)"
                        : "rgba(15,23,42,0.02)",
                  }}
                >
                  <div className="flex items-center gap-2 text-sm font-semibold">
                    <div className="p-2 rounded-xl bg-black/5">
                      <CreditCard className="w-4 h-4" />
                    </div>
                    <span>{method.name}</span>
                  </div>
                  <p className="text-xl font-bold">
                    {formatCurrency(method.total || 0)}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-slate-500">
              Aucun encaissement enregistré.
            </p>
          )}
        </section>
      </div>

      {showOpenOrders && (
        <div
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              setSelectedOpenTable(null);
              setShowOpenOrders(false);
            }
          }}
          className="fixed inset-0 z-50 flex items-end justify-center bg-slate-950/75 p-0 backdrop-blur-sm sm:items-center sm:p-4"
        >
          <section
            role="dialog"
            aria-modal="true"
            aria-labelledby="open-orders-title"
            className="flex max-h-[88vh] w-full max-w-2xl flex-col rounded-t-3xl border border-white/10 bg-[#0b1328] text-white shadow-2xl sm:rounded-3xl"
          >
            <div className="flex items-start justify-between gap-3 border-b border-white/10 p-4 sm:p-5">
              <div>
                <p className="text-xs uppercase tracking-[0.3em] text-amber-300">À encaisser</p>
                <h2 id="open-orders-title" className="mt-1 text-xl font-bold">
                  {selectedOpenTable
                    ? (selectedOpenTable.tableNumber ? `Table ${selectedOpenTable.tableNumber}` : selectedOpenTable.tableName || "Détail")
                    : "Tables occupées"}
                </h2>
                <p className="mt-1 text-sm text-white/60">
                  {selectedOpenTable
                    ? formatCurrency(selectedOpenTable.outstandingTotal)
                    : `${numberFormatter.format(stats?.openOrders?.count || 0)} table${Number(stats?.openOrders?.count || 0) > 1 ? "s" : ""} · ${formatCurrency(stats?.openOrders?.value || 0)}`}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {selectedOpenTable ? <button type="button" aria-label="Retour" onClick={() => setSelectedOpenTable(null)} className="rounded-xl border border-white/10 p-2 text-white/70 hover:bg-white/10"><ChevronLeft className="h-5 w-5" /></button> : null}
                <button type="button" aria-label="Fermer" onClick={() => { setSelectedOpenTable(null); setShowOpenOrders(false); }} className="rounded-xl border border-white/10 p-2 text-white/70 hover:bg-white/10 hover:text-white"><X className="h-5 w-5" /></button>
              </div>
            </div>
            <div className="min-h-0 space-y-2 overflow-y-auto p-3 sm:p-5">
              {selectedOpenTable ? <>
                <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                  <p className="text-xs uppercase tracking-[0.25em] text-white/45">Serveur</p>
                  <p className="mt-1 font-semibold">{selectedOpenTable.serverName || "Non renseigné"}</p>
                </div>
                {(selectedOpenTable.items || []).map((item) => <div key={item.id} className="flex items-start justify-between gap-3 rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3">
                  <div className="min-w-0"><p className="font-semibold">{item.name}</p>{item.variationName ? <p className="text-xs text-white/50">{item.variationName}</p> : null}</div>
                  <p className="shrink-0 font-semibold text-amber-300">× {numberFormatter.format(item.quantity)}</p>
                </div>)}
              </> : openOrderDetails.map((order) => {
                const tableLabel = order.tableNumber
                  ? `Table ${order.tableNumber}`
                  : order.tableName || "Table non renseignée";
                return (
                  <button
                    type="button"
                    onClick={() => setSelectedOpenTable(order)}
                    key={order.id}
                    className="flex w-full items-center justify-between gap-3 rounded-2xl border border-amber-300/15 bg-amber-300/5 px-4 py-3 text-left"
                  >
                    <div className="min-w-0">
                      <p className="break-words font-semibold">{tableLabel}</p>
                    </div>
                    <p className="shrink-0 font-bold text-amber-300">
                      {formatCurrency(order.outstandingTotal)}
                    </p>
                  </button>
                );
              })}
            </div>
          </section>
        </div>
      )}
    </div>
  );
};

const KpiCard = ({ title, value, delta, meta = null, icon: Icon, isDarkMode }) => (
  <div
    className={`rounded-3xl p-5 shadow-lg border ${
      isDarkMode ? "bg-[#0b162c] border-white/10" : "bg-white border-slate-100"
    }`}
  >
    <div className="flex items-center justify-between mb-4">
      <div>
        <p className="text-xs uppercase tracking-[0.3em] text-amber-300">
          {title}
        </p>
        <p className="text-2xl font-bold mt-1">{value}</p>
      </div>
      <div
        className={`p-3 rounded-2xl ${
          isDarkMode ? "bg-white/5" : "bg-slate-100"
        }`}
      >
        <Icon className="w-5 h-5" />
      </div>
    </div>
    {meta && <p className="text-[11px] text-slate-400 mt-2">{meta}</p>}
    {delta}
  </div>
);

const KpiDelta = ({ entry }) => {
  if (!entry || entry.change === null || entry.changePercent === null) {
    return <span className="text-xs text-slate-400">—</span>;
  }
  const positive = entry.change >= 0;
  const Icon = positive ? TrendingUpIcon : TrendingDownIcon;
  return (
    <span
      className={`inline-flex items-center gap-1 text-xs font-semibold ${
        positive ? "text-emerald-500" : "text-rose-500"
      }`}
    >
      <Icon className="w-3 h-3" />
      {`${positive ? "+" : ""}${entry.changePercent.toFixed(1)}% vs veille`}
    </span>
  );
};

const DashboardTooltip = ({ active, payload, label, activeMetrics, isDarkMode }) => {
  if (!active || !payload || !payload.length) {
    return null;
  }
  const values = payload.reduce((acc, entry) => {
    acc[entry.dataKey] = entry.value;
    return acc;
  }, {});
  const metrics = activeMetrics || { sales: true, orders: true, visitors: true };
  const tooltipStyle = isDarkMode
    ? "border-white/10 bg-slate-900/95 text-white"
    : "border-slate-200 bg-white text-slate-900";

  return (
    <div className={`rounded-2xl border px-4 py-3 shadow-lg ${tooltipStyle}`}>
      <p className="text-sm font-semibold mb-2">{label}</p>
      <div className="space-y-1 text-xs">
        {metrics.sales && (
          <p>Ventes : {formatCurrency(values.sales || 0)}</p>
        )}
        {metrics.orders && (
          <p>Commandes : {numberFormatter.format(values.orders || 0)}</p>
        )}
        {metrics.visitors && (
          <p>Visiteurs : {numberFormatter.format(values.visitors || 0)}</p>
        )}
      </div>
    </div>
  );
};

export default AdminInterface;
